import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Checkbox,
  FormGroup,
  Avatar,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Stack,
} from '@mui/material';
import { authService } from '../services';
import {
  companyConfigService,
  type PixKeyType,
  type CompanyPlan,
  type CompanyModulesConfig,
  type LoyaltyTemplatesConfig,
} from '../services/companyConfigService';
import {
  COMPANY_PLAN_OPTIONS,
  derivePlanCapabilities,
} from '../constants/companyPlans';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const DEFAULT_LOYALTY_TIMEZONE = 'America/Sao_Paulo';
const DEFAULT_LOYALTY_TRIGGER_HOUR = '18:00';

type LoyaltyTemplatesForm = {
  welcomeTemplate: string;
  abandonedCartTemplate: string;
  reactivationTemplate: string;
  launchTemplate: string;
  progressTemplate: string;
};

const buildLoyaltyTemplatesForm = (templates?: LoyaltyTemplatesConfig | null): LoyaltyTemplatesForm => ({
  welcomeTemplate: templates?.welcomeTemplate ?? '',
  abandonedCartTemplate: templates?.abandonedCartTemplate ?? '',
  reactivationTemplate: templates?.reactivationTemplate ?? '',
  launchTemplate: templates?.launchTemplate ?? '',
  progressTemplate: templates?.progressTemplate ?? '',
});

const LOYALTY_TIMEZONE_OPTIONS = [
  { value: 'America/Sao_Paulo', label: 'America/São Paulo (GMT-03:00)' },
  { value: 'America/Fortaleza', label: 'America/Fortaleza (GMT-03:00)' },
  { value: 'America/Recife', label: 'America/Recife (GMT-03:00)' },
  { value: 'America/Manaus', label: 'America/Manaus (GMT-04:00)' },
  { value: 'America/Boa_Vista', label: 'America/Boa Vista (GMT-04:00)' },
];

const LOYALTY_TEMPLATE_FIELDS: Array<{
  templateKey: keyof LoyaltyTemplatesForm;
  label: string;
  placeholder: string;
  helper: string;
}> = [
  {
    templateKey: 'welcomeTemplate',
    label: 'Template de boas-vindas',
    placeholder: 'boas_vindas_primeiro_pedido',
    helper: 'Variáveis: nome, empresa_nome, data_limite, link_oferta, company_phone',
  },
  {
    templateKey: 'abandonedCartTemplate',
    label: 'Template de recuperação de carrinho',
    placeholder: 'recuperacao_carrinho_abandonado',
    helper: 'Variáveis: nome, cupom, desconto, hora_limite, link_checkout, company_phone',
  },
  {
    templateKey: 'reactivationTemplate',
    label: 'Template de reativação',
    placeholder: 'reativacao_cliente_inativo',
    helper: 'Variáveis: nome, beneficio, data_limite, cupom, link_pedido, company_phone',
  },
  {
    templateKey: 'launchTemplate',
    label: 'Template de lançamento',
    placeholder: 'lancamento_produto',
    helper: 'Variáveis: nome, produto_novo, beneficio, link_produto, company_phone',
  },
  {
    templateKey: 'progressTemplate',
    label: 'Template de progresso / resgate',
    placeholder: 'resgate_fidelidade',
    helper:
      'Variáveis: nome, empresa_nome, pedidos_realizados, pedidos_meta, barra_progresso, pedidos_restantes, recompensa, data_limite, link_fidelidade, company_phone',
  },
];

const formatCnpj = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

const formatCep = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatWhatsapp = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';

  // Formato brasileiro: (DD) XXXXX-XXXX ou (DD) XXXX-XXXX
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  // 11 dígitos (celular): (DD) 9XXXX-XXXX
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

interface CompanyConfigForm {
  cnpj: string;
  razaoSocial: string;
  slug: string;
  customerPortalUrl: string;
  logoUrl?: string;
  whatsappPhone: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  cep: string;
  complement: string;
  message: string;
  active: boolean;
  isOpen: boolean;
  startDate: string;
  workingDays: number[];
  plan: CompanyPlan;
}

type ModulesForm = {
  paymentsEnabled: boolean;
  paymentsPixKey: string;
  paymentsPixKeyType: PixKeyType | '';
  inventoryEnabled: boolean;
  loyaltyEnabled: boolean;
  loyaltyTimezone: string;
  loyaltyDefaultTriggerHour: string;
  loyaltyTemplates: LoyaltyTemplatesForm;
};

type ModulesChangeField =
  | 'paymentsEnabled'
  | 'paymentsPixKey'
  | 'paymentsPixKeyType'
  | 'inventoryEnabled'
  | 'loyaltyEnabled'
  | 'loyaltyTimezone'
  | 'loyaltyDefaultTriggerHour';

const PIX_KEY_TYPES: PixKeyType[] = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'];

const WORKING_DAY_OPTIONS = [
  { label: 'Seg', value: 1 },
  { label: 'Ter', value: 2 },
  { label: 'Qua', value: 3 },
  { label: 'Qui', value: 4 },
  { label: 'Sex', value: 5 },
  { label: 'Sáb', value: 6 },
  { label: 'Dom', value: 0 },
];

interface TabPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`company-tabpanel-${index}`}
    aria-labelledby={`company-tab-${index}`}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const tabA11yProps = (index: number) => ({
  id: `company-tab-${index}`,
  'aria-controls': `company-tabpanel-${index}`,
});

const CompanyManagement: React.FC = () => {
  const user = authService.getUser();
  const canEditPlan = user?.role === 'DEVELOPER';
  const [form, setForm] = useState<CompanyConfigForm>({
    cnpj: user?.cnpj || '',
    razaoSocial: '',
    slug: '',
    customerPortalUrl: '',
    logoUrl: undefined,
    whatsappPhone: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    cep: '',
    complement: '',
    message: '',
    active: true,
    isOpen: true,
    startDate: '',
    workingDays: [2, 3, 4, 5, 6, 0],
    plan: 'VITRINE_APP',
  });
  const [modulesForm, setModulesForm] = useState<ModulesForm>({
    paymentsEnabled: false,
    paymentsPixKey: '',
    paymentsPixKeyType: '',
    inventoryEnabled: false,
    loyaltyEnabled: false,
    loyaltyTimezone: DEFAULT_LOYALTY_TIMEZONE,
    loyaltyDefaultTriggerHour: DEFAULT_LOYALTY_TRIGGER_HOUR,
    loyaltyTemplates: buildLoyaltyTemplatesForm(),
  });
  const [tabIndex, setTabIndex] = useState(0);
  const [formErrors, setFormErrors] = useState<{
    cnpj?: string;
    whatsappPhone?: string;
    cep?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const planCapabilities = useMemo(() => derivePlanCapabilities(form.plan), [form.plan]);
  const selectedPlan = useMemo(
    () => COMPANY_PLAN_OPTIONS.find((option) => option.value === form.plan) ?? COMPANY_PLAN_OPTIONS[0],
    [form.plan]
  );

  const slugify = (value: string): string => {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const config = await companyConfigService.getMyConfig();
        if (config) {
          const endereco = config.endereco;
          setForm((prev) => ({
            ...prev,
            cnpj: config.cnpj || prev.cnpj,
            razaoSocial: config.razaoSocial || prev.razaoSocial,
            slug: config.slug || prev.slug,
            customerPortalUrl: config.customerPortalUrl || '',
            logoUrl: config.logoUrl || prev.logoUrl,
            whatsappPhone: config.whatsappPhone || prev.whatsappPhone,
            street: endereco?.rua || '',
            number: endereco?.numero || '',
            neighborhood: endereco?.bairro || '',
            city: endereco?.cidade || '',
            state: endereco?.estado || '',
            cep: endereco?.cep || '',
            complement: endereco?.complemento || '',
            // apiKey e hashSecret não são retornados por segurança
            message: config.message || '',
            active: config.active,
            isOpen: config.isOpen ?? true,
            startDate: config.dataInicioUsoPlataforma
              ? new Date(config.dataInicioUsoPlataforma).toISOString().slice(0, 10)
              : prev.startDate,
            workingDays:
              Array.isArray(config.diasFuncionamento) && config.diasFuncionamento.length > 0
                ? config.diasFuncionamento
                : prev.workingDays,
            plan: config.plan || prev.plan,
          }));
          const caps = derivePlanCapabilities(config.plan || 'VITRINE_APP');
          const loyaltyConfig = config.modules?.loyalty;
          setModulesForm({
            paymentsEnabled: config.modules?.payments?.enabled ?? caps.paymentsEnabled,
            paymentsPixKey: config.modules?.payments?.pixKey ?? '',
            paymentsPixKeyType: config.modules?.payments?.pixKeyType ?? '',
            inventoryEnabled: config.modules?.inventory?.enabled ?? caps.inventoryEnabled,
            loyaltyEnabled: caps.loyaltyEnabled
              ? loyaltyConfig?.enabled ?? caps.loyaltyEnabled
              : false,
            loyaltyTimezone: loyaltyConfig?.timezone ?? DEFAULT_LOYALTY_TIMEZONE,
            loyaltyDefaultTriggerHour: loyaltyConfig?.defaultTriggerHour ?? DEFAULT_LOYALTY_TRIGGER_HOUR,
            loyaltyTemplates: buildLoyaltyTemplatesForm(loyaltyConfig?.templates),
          });
        }
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar configuração da empresa');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  const handleChange = (field: keyof CompanyConfigForm, value: string | boolean) => {
    setForm((prev) => {
      let nextValue: string | boolean = value;

      // Sanitizar campos numéricos críticos para apenas dígitos
      if (typeof value === 'string') {
        if (field === 'cnpj' || field === 'whatsappPhone' || field === 'cep') {
          let digits = value.replace(/\D/g, '');
          if (field === 'cnpj') {
            digits = digits.slice(0, 14);
          } else if (field === 'cep') {
            digits = digits.slice(0, 8);
          } else if (field === 'whatsappPhone') {
            digits = digits.slice(0, 11);
          }
          nextValue = digits;
        } else if (field === 'customerPortalUrl') {
          nextValue = value.trim();
        }
      }

      const next: CompanyConfigForm = { ...prev, [field]: nextValue } as CompanyConfigForm;

      if (field === 'razaoSocial') {
        const text = String(value || '');
        next.slug = text ? slugify(text) : '';
      }

      if (field === 'plan') {
        const caps = derivePlanCapabilities(next.plan);
        setModulesForm((prevModules) => ({
          ...prevModules,
          paymentsEnabled: caps.paymentsEnabled ? prevModules.paymentsEnabled : false,
          inventoryEnabled: caps.inventoryEnabled ? prevModules.inventoryEnabled : false,
          loyaltyEnabled: caps.loyaltyEnabled ? prevModules.loyaltyEnabled : false,
        }));
      }

      return next;
    });

    // Limpar erro desse campo ao alterar
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleToggleWorkingDay = (day: number) => {
    setForm((prev) => {
      const exists = prev.workingDays.includes(day);
      const workingDays = exists
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day];
      return { ...prev, workingDays };
    });
  };

  const handleModulesChange = (field: ModulesChangeField, value: string | boolean) => {
    if (field === 'paymentsEnabled' && !planCapabilities.paymentsEnabled) {
      return;
    }
    if (field === 'inventoryEnabled' && !planCapabilities.inventoryEnabled) {
      return;
    }
    if (field === 'loyaltyEnabled' && !planCapabilities.loyaltyEnabled) {
      return;
    }

    let nextValue: string | boolean = value;
    if (typeof nextValue === 'string') {
      nextValue = nextValue.trim();
      if (field === 'loyaltyDefaultTriggerHour' && nextValue.length > 5) {
        nextValue = nextValue.slice(0, 5);
      }
    }

    setModulesForm((prev) => ({
      ...prev,
      [field]: nextValue as ModulesForm[typeof field],
    }));
  };

  const handleLoyaltyTemplateChange = (key: keyof LoyaltyTemplatesForm, value: string) => {
    setModulesForm((prev) => ({
      ...prev,
      loyaltyTemplates: {
        ...prev.loyaltyTemplates,
        [key]: value,
      },
    }));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // Validações básicas de formato antes de enviar
    const newErrors: typeof formErrors = {};

    const cnpjDigits = (form.cnpj || '').replace(/\D/g, '');
    if (!cnpjDigits || cnpjDigits.length !== 14) {
      newErrors.cnpj = 'CNPJ deve conter exatamente 14 dígitos.';
    }

    const whatsappDigits = (form.whatsappPhone || '').replace(/\D/g, '');
    if (whatsappDigits && whatsappDigits.length !== 10 && whatsappDigits.length !== 11) {
      newErrors.whatsappPhone = 'WhatsApp deve ter 10 ou 11 dígitos (com DDD).';
    }

    const cepDigits = (form.cep || '').replace(/\D/g, '');
    if (cepDigits && cepDigits.length !== 8) {
      newErrors.cep = 'CEP deve conter exatamente 8 dígitos.';
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const trimmedPixKey = modulesForm.paymentsPixKey.trim();
      const trimmedTemplates = Object.entries(modulesForm.loyaltyTemplates).reduce(
        (acc, [key, template]) => {
          const trimmed = template.trim();
          if (trimmed.length > 0) {
            acc[key as keyof LoyaltyTemplatesConfig] = trimmed;
          }
          return acc;
        },
        {} as Partial<LoyaltyTemplatesConfig>,
      );

      const modulesPayload: CompanyModulesConfig = {
        payments: {
          enabled: modulesForm.paymentsEnabled,
          pixKey: trimmedPixKey || undefined,
          pixKeyType: modulesForm.paymentsPixKeyType || undefined,
        },
        inventory: {
          enabled: modulesForm.inventoryEnabled,
        },
      };

      if (planCapabilities.loyaltyEnabled) {
        modulesPayload.loyalty = {
          enabled: modulesForm.loyaltyEnabled,
          timezone: modulesForm.loyaltyTimezone || undefined,
          defaultTriggerHour: modulesForm.loyaltyDefaultTriggerHour || undefined,
          templates: Object.keys(trimmedTemplates).length > 0 ? trimmedTemplates : undefined,
        };
      }

      // Montar string de endereço a partir dos campos estruturados
      const updated = await companyConfigService.updateMyConfig({
        isOpen: form.isOpen,
        active: form.active,
        razaoSocial: form.razaoSocial || undefined,
        slug: form.slug || undefined,
        customerPortalUrl: form.customerPortalUrl ? form.customerPortalUrl.trim() : undefined,
        whatsappPhone: form.whatsappPhone || undefined,
        endereco: {
          rua: form.street,
          numero: form.number,
          bairro: form.neighborhood,
          cidade: form.city,
          estado: form.state,
          cep: form.cep,
          complemento: form.complement,
        },
        message: form.message,
        dataInicioUsoPlataforma: form.startDate ? new Date(form.startDate).toISOString() : undefined,
        diasFuncionamento: form.workingDays,
        plan: canEditPlan ? form.plan : undefined,
        modules: modulesPayload,
      });
      setSuccess('Configurações salvas com sucesso.');
      const nextPlan = updated.plan ?? form.plan;
      setForm((prev) => ({
        ...prev,
        active: updated.active,
        isOpen: updated.isOpen ?? prev.isOpen,
        razaoSocial: updated.razaoSocial || prev.razaoSocial,
        slug: updated.slug || prev.slug,
        customerPortalUrl: updated.customerPortalUrl || prev.customerPortalUrl,
        logoUrl: updated.logoUrl || prev.logoUrl,
        whatsappPhone: updated.whatsappPhone || prev.whatsappPhone,
        street: updated.endereco?.rua || prev.street,
        number: updated.endereco?.numero || prev.number,
        neighborhood: updated.endereco?.bairro || prev.neighborhood,
        city: updated.endereco?.cidade || prev.city,
        state: updated.endereco?.estado || prev.state,
        cep: updated.endereco?.cep || prev.cep,
        complement: updated.endereco?.complemento || prev.complement,
        message: updated.message || prev.message,
        startDate: updated.dataInicioUsoPlataforma
          ? new Date(updated.dataInicioUsoPlataforma).toISOString().slice(0, 10)
          : prev.startDate,
        workingDays:
          Array.isArray(updated.diasFuncionamento) && updated.diasFuncionamento.length > 0
            ? updated.diasFuncionamento
            : prev.workingDays,
        plan: nextPlan,
      }));
      const caps = derivePlanCapabilities(nextPlan);
      const updatedLoyalty = updated.modules?.loyalty;
      setModulesForm({
        paymentsEnabled: updated.modules?.payments?.enabled ?? caps.paymentsEnabled,
        paymentsPixKey: updated.modules?.payments?.pixKey ?? '',
        paymentsPixKeyType: updated.modules?.payments?.pixKeyType ?? '',
        inventoryEnabled: updated.modules?.inventory?.enabled ?? caps.inventoryEnabled,
        loyaltyEnabled: caps.loyaltyEnabled
          ? updatedLoyalty?.enabled ?? caps.loyaltyEnabled
          : false,
        loyaltyTimezone: updatedLoyalty?.timezone ?? DEFAULT_LOYALTY_TIMEZONE,
        loyaltyDefaultTriggerHour: updatedLoyalty?.defaultTriggerHour ?? DEFAULT_LOYALTY_TRIGGER_HOUR,
        loyaltyTemplates: buildLoyaltyTemplatesForm(updatedLoyalty?.templates),
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Configuração da Empresa
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Defina os dados da empresa, chaves de integração e se o estabelecimento está aberto para pedidos.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab label="Dados da empresa" {...tabA11yProps(0)} />
              <Tab label="Funcionamento" {...tabA11yProps(1)} />
              <Tab label="Módulos contratados" {...tabA11yProps(2)} />
            </Tabs>
          </Box>

          <TabPanel value={tabIndex} index={0}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar src={form.logoUrl} sx={{ width: 56, height: 56 }}>
                  {form.razaoSocial?.charAt(0) || 'E'}
                </Avatar>
                <Button variant="outlined" component="label">
                  Enviar logo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (e) => {
                      if (!e.target.files || e.target.files.length === 0) return;
                      const file = e.target.files[0];
                      try {
                        setLoading(true);
                        const result = await companyConfigService.uploadLogo(file);
                        setForm((prev) => ({ ...prev, logoUrl: result.logoUrl || prev.logoUrl }));
                        setSuccess('Logo atualizada com sucesso.');
                      } catch (err: any) {
                        setError(err.message || 'Erro ao enviar logo');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  />
                </Button>
              </Box>

              <TextField
                label="Razão social"
                value={form.razaoSocial}
                onChange={(e) => handleChange('razaoSocial', e.target.value)}
                fullWidth
              />
              <TextField
                label="Slug público (URL da loja)"
                value={form.slug}
                helperText="Será usado na URL pública do delivery. Use apenas letras minúsculas, números e hífens."
                disabled
                fullWidth
              />
              <TextField
                label="URL do portal do cliente"
                value={form.customerPortalUrl}
                onChange={(e) => handleChange('customerPortalUrl', e.target.value)}
                fullWidth
                placeholder="https://minhaloja.com.br"
              />
              <TextField
                label="CNPJ"
                value={formatCnpj(form.cnpj)}
                onChange={(e) => handleChange('cnpj', e.target.value)}
                fullWidth
                required
                error={!!formErrors.cnpj}
                helperText={formErrors.cnpj}
              />

              <TextField
                label="WhatsApp da empresa (somente números, com DDD)"
                value={formatWhatsapp(form.whatsappPhone)}
                onChange={(e) => handleChange('whatsappPhone', e.target.value)}
                fullWidth
                error={!!formErrors.whatsappPhone}
                helperText={formErrors.whatsappPhone}
              />

              <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                <TextField
                  select
                  label="Plano contratado"
                  value={form.plan}
                  onChange={(e) => handleChange('plan', e.target.value as CompanyPlan)}
                  disabled={!canEditPlan}
                  sx={{ minWidth: { md: 260 } }}
                >
                  {COMPANY_PLAN_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Chip
                  label={selectedPlan.focus}
                  color={form.plan === 'WHITE_LABEL_BUSINESS' ? 'secondary' : 'default'}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, fontWeight: 600 }}
                />
              </Box>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedPlan.monthlyPrice}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
                    {selectedPlan.setupFee}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedPlan.description}
                </Typography>
                <Stack component="ul" spacing={1.25} sx={{ mt: 2, pl: 0, listStyle: 'none' }}>
                  {selectedPlan.features.map((feature) => (
                    <Stack
                      key={feature}
                      component="li"
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 18, mt: 0.25 }} />
                      <Typography variant="body2" color="text.secondary">
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField
                  label="Rua"
                  value={form.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Número"
                  value={form.number}
                  onChange={(e) => handleChange('number', e.target.value)}
                  sx={{ width: { xs: '100%', sm: '160px' } }}
                />
              </Box>

              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField
                  label="Bairro"
                  value={form.neighborhood}
                  onChange={(e) => handleChange('neighborhood', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Cidade"
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  fullWidth
                />
              </Box>

              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField
                  label="Estado"
                  value={form.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  sx={{ width: { xs: '100%', sm: '120px' } }}
                />
                <TextField
                  label="CEP"
                  value={formatCep(form.cep)}
                  onChange={(e) => handleChange('cep', e.target.value)}
                  sx={{ width: { xs: '100%', sm: '200px' } }}
                  error={!!formErrors.cep}
                  helperText={formErrors.cep}
                />
                <TextField
                  label="Complemento"
                  value={form.complement}
                  onChange={(e) => handleChange('complement', e.target.value)}
                  fullWidth
                />
              </Box>

              <TextField
                label="Mensagem da loja / aviso (opcional)"
                value={form.message}
                onChange={(e) => handleChange('message', e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabIndex} index={1}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Início de uso da plataforma
                </Typography>
                <TextField
                  type="date"
                  value={form.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Dias de funcionamento (delivery)
                </Typography>
                <FormGroup row sx={{ gap: 1.5 }}>
                  {WORKING_DAY_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={form.workingDays.includes(option.value)}
                          onChange={() => handleToggleWorkingDay(option.value)}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
              </Box>

              <Box display="flex" flexDirection="column" gap={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.active}
                      onChange={(e) => handleChange('active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Empresa ativa"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isOpen}
                      onChange={(e) => handleChange('isOpen', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Estabelecimento aberto para pedidos"
                />
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabIndex} index={2}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">Pagamentos online</Typography>
                    <Switch
                      checked={modulesForm.paymentsEnabled}
                      onChange={(e) => handleModulesChange('paymentsEnabled', e.target.checked)}
                      color="primary"
                      disabled={!planCapabilities.paymentsEnabled}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Configure seu provedor de pagamento online e personalize a chave PIX que receberá os repasses.
                  </Typography>
                  <TextField
                    label="Chave PIX para repasse"
                    value={modulesForm.paymentsPixKey}
                    onChange={(e) => handleModulesChange('paymentsPixKey', e.target.value)}
                    fullWidth
                    disabled={!modulesForm.paymentsEnabled}
                  />
                  <TextField
                    select
                    label="Tipo da chave PIX"
                    value={modulesForm.paymentsPixKeyType}
                    onChange={(e) => handleModulesChange('paymentsPixKeyType', e.target.value as PixKeyType | '')}
                    disabled={!modulesForm.paymentsEnabled}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>Selecionar</em>
                    </MenuItem>
                    {PIX_KEY_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">Controle de estoque</Typography>
                  <Switch
                    checked={modulesForm.inventoryEnabled}
                    onChange={(e) => handleModulesChange('inventoryEnabled', e.target.checked)}
                    color="secondary"
                    disabled={!planCapabilities.inventoryEnabled}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Permite lançar saídas e acompanhar o saldo de estoque quando o plano contratado inclui o módulo.
                </Typography>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: modulesForm.loyaltyEnabled ? '#10B981' : undefined,
                  backgroundColor: modulesForm.loyaltyEnabled ? 'rgba(16, 185, 129, 0.05)' : 'transparent',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">Fidelidade e campanhas</Typography>
                  <Switch
                    checked={modulesForm.loyaltyEnabled}
                    onChange={(e) => handleModulesChange('loyaltyEnabled', e.target.checked)}
                    color="success"
                    disabled={!planCapabilities.loyaltyEnabled}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure o fuso horário, horário padrão de disparo e os templates aprovados no WhatsApp para as
                  campanhas de fidelidade desta empresa.
                </Typography>

                {!planCapabilities.loyaltyEnabled && (
                  <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    O plano atual não libera o módulo de fidelidade. Atualize o plano para editar essas configurações.
                  </Alert>
                )}

                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} mb={2}>
                  <TextField
                    select
                    fullWidth
                    label="Fuso horário das campanhas"
                    value={modulesForm.loyaltyTimezone}
                    onChange={(event) => handleModulesChange('loyaltyTimezone', event.target.value)}
                    disabled={!planCapabilities.loyaltyEnabled || !modulesForm.loyaltyEnabled}
                  >
                    {LOYALTY_TIMEZONE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Horário padrão de disparo"
                    type="time"
                    value={modulesForm.loyaltyDefaultTriggerHour}
                    onChange={(event) => handleModulesChange('loyaltyDefaultTriggerHour', event.target.value)}
                    disabled={!planCapabilities.loyaltyEnabled || !modulesForm.loyaltyEnabled}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: { md: 220 } }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Os nomes dos templates devem corresponder exatamente aos aprovados no Meta Business para evitar
                  falhas de envio.
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  {LOYALTY_TEMPLATE_FIELDS.map((field) => (
                    <Box key={field.templateKey}>
                      <TextField
                        label={field.label}
                        value={modulesForm.loyaltyTemplates[field.templateKey]}
                        onChange={(event) => handleLoyaltyTemplateChange(field.templateKey, event.target.value)}
                        placeholder={field.placeholder}
                        helperText={field.helper}
                        fullWidth
                        disabled={!planCapabilities.loyaltyEnabled || !modulesForm.loyaltyEnabled}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          </TabPanel>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar configurações'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CompanyManagement;
